import React, { useState, useEffect } from "react";
import ScheduleTest from "../components/ScheduleTest";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Stack, Inline } from '@/components/ui/layout';
import { Heading, Text } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { cn } from '@/lib/utils';
import { Search, Users } from 'lucide-react';

const UserDashboard = () => {
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Fetch users from the backend
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/select_users"); // Adjust the endpoint to your backend API

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
      alert("Failed to fetch users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle user selection
  const handleSelectUser = (userId) => {
    const updatedSelection = selectedUsers.includes(userId)
      ? selectedUsers.filter((id) => id !== userId) // Deselect
      : [...selectedUsers, userId]; // Select
    setSelectedUsers(updatedSelection);
  };

  const handleNextButtonClick = () => {
    if (selectedUsers.length === 0) {
      alert("Please select at least one user.");
      return;
    }
    setIsPopupVisible(true); // Show the popup
  };

  const handleClosePopup = () => {
    setIsPopupVisible(false); // Close the popup
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 p-5 min-h-[600px]">
      {/* Left Section: Search and Inputs */}
      <Card className="flex-1">
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack spacing="md">
            <div>
              <Label htmlFor="emailInput">Search By Email</Label>
              <Input
                type="email"
                id="emailInput"
                placeholder="Search By Email"
                aria-label="Search By Email"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="experienceInput">Experience Year</Label>
              <Input
                type="text"
                id="experienceInput"
                placeholder="Experience Year"
                aria-label="Experience Year"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="skillInput">Enter Skill</Label>
              <Input
                type="text"
                id="skillInput"
                placeholder="Enter Skill"
                aria-label="Enter Skill"
                className="mt-1"
              />
            </div>

            <Button className="w-full">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Right Section: User List */}
      <Card
        className="flex-1 max-w-[400px] relative overflow-hidden"
        style={{
          backgroundImage: "url('/root@3x.png')",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Text className="text-white">Loading users...</Text>
          ) : (
            <Stack spacing="sm">
              <div className="flex items-center gap-2 p-2 bg-white/10 rounded">
                <Checkbox
                  checked={users.length > 0 && users.length === selectedUsers.length}
                  onCheckedChange={(checked) => {
                    setSelectedUsers(checked ? users.map((user) => user.id) : []);
                  }}
                />
                <Label className="text-white cursor-pointer">Select all Users</Label>
              </div>
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-2 p-2 bg-white/10 rounded hover:bg-white/20 transition-colors">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={() => handleSelectUser(user.id)}
                    />
                    <Label htmlFor={`user-${user.id}`} className="text-white cursor-pointer flex-1">
                      {user.email}
                    </Label>
                  </div>
                ))}
              </div>
            </Stack>
          )}
          <Button
            className="w-full mt-4"
            onClick={handleNextButtonClick}
            disabled={selectedUsers.length === 0}
          >
            Next: Schedule Test
          </Button>
        </CardContent>
      </Card>

      <Dialog open={isPopupVisible} onOpenChange={setIsPopupVisible}>
        <DialogContent>
          <ScheduleTest onClose={handleClosePopup} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDashboard;
